import { useState } from "react";
import { Settings, CircleUser } from "lucide-react";
import { Card } from "../../components/shared/Card";
import { PageHeader } from "../../components/shared/PageHeader";
import UserManagement from "./UserManagement";

/* Settings — super_admin-only configuration panel. Six tabs:
     1. General — placeholder
     2. API Integrations — placeholder
     3. SMTP — placeholder
     4. Roles & Permissions — read-only matrix of module × role
     5. Audit Logs — list of recent admin actions
     6. Users — invite / list / change role / deactivate (Phase 1)

   Phase 1.5 changes:
   - Added Users tab (real, backed by /v1/users + /v1/invitations APIs)
   - Renamed "Sales" column → "Employee" in the Roles & Permissions matrix
   - Mock Audit Logs sample updated to reflect the rename

   Route gating to super_admin already happens at the sidebar (NAV
   entry has `roles: ["super_admin"]`), so this view assumes the user
   reaching it is a super_admin. */
export function SettingsView() {
  const tabs = ["General", "API Integrations", "SMTP", "Roles & Permissions", "Audit Logs", "Users"];
  const [tab, setTab] = useState(0);

  return (
    <div>
      <PageHeader accent="ops" title="Settings" subtitle="Super Admin only" />

      <div className="flex gap-1 mb-5 border-b border-slate-200 overflow-x-auto">
        {tabs.map((t, i) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`text-sm font-medium px-3 py-2 border-b-2 -mb-px shrink-0 ${tab === i ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 3 ? (
        /* Roles & Permissions matrix. Module × Role grid showing what
           each role can see / do per module. Read-only for now —
           writing to this becomes a settings API endpoint later. */
        <Card>
          <div className="overflow-x-auto -mx-3 sm:mx-0"><table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-xs font-medium text-slate-500 bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-2.5">Module</th>
                <th className="px-3 py-2.5">Super Admin</th>
                <th className="px-3 py-2.5">Admin</th>
                <th className="px-3 py-2.5">Employee</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[
                ["Properties",         "Full", "Full",                "View"],
                ["Real Estate Leads",  "Full", "Full",                "Assigned only"],
                ["Interior Leads",     "Full", "Full",                "None"],
                ["CMS Homepage",       "Full", "Full",                "None"],
                ["Blog",               "Full", "Full",                "None"],
                ["User Management",    "Full", "Invite employees",    "None"],
                ["Platform Settings",  "Full", "None",                "None"],
              ].map((r) => (
                <tr key={r[0]} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 font-medium text-slate-900">{r[0]}</td>
                  {r.slice(1).map((c, i) => (
                    <td key={i} className="px-3 py-2.5 text-slate-600">{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table></div>
        </Card>
      ) : tab === 4 ? (
        /* Audit Logs preview. Latest few admin actions; will pull from
           an actual audit table once the backend is wired. Shape is
           intentionally simple (actor / action / when) so the UI doesn't
           need to change much when real data lands. */
        <Card className="divide-y divide-slate-100">
          {[
            ["Sarika K.",   "Revealed phone for lead L1003",      "2 min ago"],
            ["Admin",       "Published project Godrej Splendour", "1 h ago"],
            ["Kedar Naik",  "Changed status L1012 → Site Visit",  "3 h ago"],
            ["Super Admin", "Added employee Aditya Rao",          "Yesterday"],
          ].map((r, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 text-sm">
              <CircleUser className="w-4 h-4 text-slate-400" />
              <span className="font-medium text-slate-900 w-28 shrink-0">{r[0]}</span>
              <span className="flex-1 text-slate-600">{r[1]}</span>
              <span className="text-xs text-slate-400">{r[2]}</span>
            </div>
          ))}
        </Card>
      ) : tab === 5 ? (
        /* Users tab — list, invite, change role, deactivate. Real
           backend wiring via /v1/users + /v1/invitations. */
        <UserManagement />
      ) : (
        /* Placeholder for tabs 0 / 1 / 2 (General / API Integrations /
           SMTP). Ship as visual stubs until the corresponding API
           endpoints + config forms are ready. */
        <Card className="p-12 grid place-items-center text-center">
          <Settings className="w-8 h-8 text-slate-300 mb-2" />
          <p className="text-sm text-slate-400">{tabs[tab]} configuration panel</p>
        </Card>
      )}
    </div>
  );
}
